import { useCallback, useMemo, useReducer } from 'react'
import { MOCK_PRESETS } from '../_mock-data/library'
import type { FilterNode, LibraryItem } from './_lib/filter-engine'
import type { SortSpec } from './_lib/sorting'
import type {
  CurriculumSlice,
  CustomCardInput,
  LibraryTab,
  TypeFilter,
} from './_state/contracts'
import { createInitialState } from './_state/initial-state'
import { curriculumReducer } from './_state/reducer'
import {
  activePreset,
  placingCard,
  dayUsedHours as selectDayUsedHours,
} from './_state/selectors'

export type {
  CompanyTag,
  CurriculumSlice,
  CustomCardInput,
  LibraryTab,
  TypeFilter,
} from './_state/contracts'

export function useCurriculumSlice(): CurriculumSlice {
  const [state, dispatch] = useReducer(
    curriculumReducer,
    undefined,
    createInitialState,
  )

  const dismissFlash = useCallback(
    () => dispatch({ type: 'dismiss-flash' }),
    [],
  )
  const addAuto = useCallback(
    (item: LibraryItem, sourceId?: string | null) =>
      dispatch({ type: 'add-auto', item, sourceId: sourceId ?? null }),
    [],
  )
  const stageToTray = useCallback(
    (item: LibraryItem, sourceId?: string | null) =>
      dispatch({ type: 'stage-to-tray', item, sourceId: sourceId ?? null }),
    [],
  )
  const removeFromTray = useCallback(
    (cardId: string) => dispatch({ type: 'remove-from-tray', cardId }),
    [],
  )
  const togglePlacing = useCallback(
    (cardId: string) => dispatch({ type: 'toggle-placing', cardId }),
    [],
  )
  const cancelPlacing = useCallback(
    () => dispatch({ type: 'cancel-placing' }),
    [],
  )
  const placeAt = useCallback(
    (weekIdx: number, dayIdx: number) =>
      dispatch({ type: 'place-at', weekIdx, dayIdx }),
    [],
  )
  const placeCardAt = useCallback(
    (cardId: string, weekIdx: number, dayIdx: number) =>
      dispatch({ type: 'place-card-at', cardId, weekIdx, dayIdx }),
    [],
  )
  const removeFromDay = useCallback(
    (cardId: string) => dispatch({ type: 'remove-from-day', cardId }),
    [],
  )
  const moveCardTo = useCallback(
    (cardId: string, weekIdx: number, dayIdx: number) =>
      dispatch({ type: 'move-card-to', cardId, weekIdx, dayIdx }),
    [],
  )
  const distributeTray = useCallback(
    () => dispatch({ type: 'distribute-tray' }),
    [],
  )
  const optimizeSchedule = useCallback(
    () => dispatch({ type: 'optimize-schedule' }),
    [],
  )
  const publishWeek = useCallback(
    (weekId: string) => dispatch({ type: 'publish-week', weekId }),
    [],
  )
  const unpublishWeek = useCallback(
    (weekId: string) => dispatch({ type: 'unpublish-week', weekId }),
    [],
  )
  const deleteWeek = useCallback(
    (weekId: string) => dispatch({ type: 'delete-week', weekId }),
    [],
  )
  const addWeek = useCallback(() => dispatch({ type: 'add-week' }), [])
  const setBudget = useCallback(
    (weekIdx: number, dayIdx: number, budget: number) =>
      dispatch({ type: 'set-budget', weekIdx, dayIdx, budget }),
    [],
  )
  const toggleWeek = useCallback(
    (weekId: string) => dispatch({ type: 'toggle-week', weekId }),
    [],
  )
  const addCustomCard = useCallback(
    (weekIdx: number, dayIdx: number, card: CustomCardInput) =>
      dispatch({ type: 'add-custom-card', weekIdx, dayIdx, card }),
    [],
  )
  const convertToAiMock = useCallback(
    (cardId: string) => dispatch({ type: 'convert-to-ai-mock', cardId }),
    [],
  )
  const revertAiMock = useCallback(
    (cardId: string) => dispatch({ type: 'revert-ai-mock', cardId }),
    [],
  )
  const linkAiMockQuestion = useCallback(
    (cardId: string, item: LibraryItem) =>
      dispatch({ type: 'link-ai-mock', cardId, item }),
    [],
  )
  const addInterviewTier = useCallback(
    (tier: 'ai' | 'peer' | 'coach', destination: 'auto' | 'tray') =>
      dispatch({ type: 'add-interview-tier', tier, destination }),
    [],
  )
  const applyPreset = useCallback((id: string) => {
    const preset = MOCK_PRESETS.find((candidate) => candidate.id === id)
    if (preset) dispatch({ type: 'apply-preset', preset })
  }, [])
  const setFilters = useCallback(
    (filters: FilterNode[]) => dispatch({ type: 'set-filters', filters }),
    [],
  )
  const setSorts = useCallback(
    (sorts: SortSpec[]) => dispatch({ type: 'set-sorts', sorts }),
    [],
  )
  const setTypeFilter = useCallback(
    (filter: TypeFilter) => dispatch({ type: 'set-type-filter', filter }),
    [],
  )
  const cycleCompanyTag = useCallback(
    (company: string) => dispatch({ type: 'cycle-company-tag', company }),
    [],
  )
  const cycleSeenFilter = useCallback(
    () => dispatch({ type: 'cycle-seen-filter' }),
    [],
  )
  const toggleColumn = useCallback(
    (column: string) => dispatch({ type: 'toggle-column', column }),
    [],
  )
  const setColumnOrder = useCallback(
    (order: string[]) => dispatch({ type: 'set-column-order', order }),
    [],
  )
  const setLibraryTab = useCallback(
    (tab: LibraryTab) => dispatch({ type: 'set-library-tab', tab }),
    [],
  )
  const dayUsedHours = useCallback(
    (weekIdx: number, dayIdx: number) =>
      selectDayUsedHours(state.weeks, weekIdx, dayIdx),
    [state.weeks],
  )

  return useMemo(
    () => ({
      flash: state.flash,
      dismissFlash,
      weeks: state.weeks,
      tray: state.tray,
      filters: state.filters,
      sorts: state.sorts,
      typeFilter: state.typeFilter,
      companyTags: state.companyTags,
      hiddenCols: state.hiddenCols,
      columnOrder: state.columnOrder,
      seenFilter: state.seenFilter,
      libraryTab: state.libraryTab,
      placing: placingCard(state),
      activePresetId: activePreset(state, MOCK_PRESETS),
      addAuto,
      stageToTray,
      removeFromTray,
      togglePlacing,
      cancelPlacing,
      placeAt,
      placeCardAt,
      removeFromDay,
      moveCardTo,
      distributeTray,
      optimizeSchedule,
      publishWeek,
      unpublishWeek,
      deleteWeek,
      addWeek,
      setBudget,
      toggleWeek,
      addCustomCard,
      convertToAiMock,
      revertAiMock,
      linkAiMockQuestion,
      addInterviewTier,
      applyPreset,
      setFilters,
      setSorts,
      setTypeFilter,
      cycleCompanyTag,
      cycleSeenFilter,
      toggleColumn,
      setColumnOrder,
      setLibraryTab,
      dayUsedHours,
    }),
    [
      state,
      dismissFlash,
      addAuto,
      stageToTray,
      removeFromTray,
      togglePlacing,
      cancelPlacing,
      placeAt,
      placeCardAt,
      removeFromDay,
      moveCardTo,
      distributeTray,
      optimizeSchedule,
      publishWeek,
      unpublishWeek,
      deleteWeek,
      addWeek,
      setBudget,
      toggleWeek,
      addCustomCard,
      convertToAiMock,
      revertAiMock,
      linkAiMockQuestion,
      addInterviewTier,
      applyPreset,
      setFilters,
      setSorts,
      setTypeFilter,
      cycleCompanyTag,
      cycleSeenFilter,
      toggleColumn,
      setColumnOrder,
      setLibraryTab,
      dayUsedHours,
    ],
  )
}
